import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { computeText } from '@/locales/utils';
import { Text } from '@/components/atoms/Text';
import { Title } from '@/components/atoms/Title';
import {
    StyledText,
    Container,
    Header,
    HeaderLeft,
    HeaderRight,
    ErrorList,
    ErrorItem,
    StatsList,
    StatItem,
    SectionTitle,
    WarningItem,
    WarningList,
} from './ResultStep.style';

type ErrorDetail = {
    lineNumber: number;
    error: string;
    line: {
        firstName?: string;
        lastName?: string;
        email?: string;
        [key: string]: unknown;
    };
};

type CustomerErrorOrWarn = {
    line: number;
    email: string;
    invalidFields: string[];
};

type ImportResult = {
    customersCreated: number;
    customersUpdated: number;
    customersUnchanged: number;
    customersError: CustomerErrorOrWarn[];
    customersWarn: CustomerErrorOrWarn[];
    otherWarnings: Array<{ type: string; list: string[] }>;
    validationErrors: Array<{ lineNumber: number; error: string }>;
} | null;

export type ResultStepProps = {
    state?: 'SUCCESS' | 'ERROR';
    errorDetails?: ErrorDetail[];
    importResult?: ImportResult;
};

export const ResultStep: React.FunctionComponent<React.PropsWithChildren<ResultStepProps>> = ({
    state = 'SUCCESS',
    errorDetails = [],
    importResult,
}) => {
    const intl = useIntl();

    const stateText = useMemo(
        () => (state === 'SUCCESS' ? 'customer.modal.import.report.success' : 'customer.modal.import.report.error'),
        [state],
    );

    const formatErrorLine = (detail: ErrorDetail): string => {
        const name = [detail.line.firstName, detail.line.lastName].filter(Boolean).join(' ') || detail.line.email || '';
        const nameDisplay = name ? ` - ${name}` : '';
        return computeText(intl, 'customer.modal.import.result.lineError', {
            lineNumber: detail.lineNumber,
            nameDisplay,
            error: detail.error,
        });
    };

    const formatCustomerError = (item: CustomerErrorOrWarn): string => {
        const fields = item.invalidFields.join(', ');
        return computeText(intl, 'customer.modal.import.result.fieldError', {
            fields,
            line: item.line,
        });
    };

    const hasErrors = errorDetails.length > 0 || (importResult?.customersError?.length ?? 0) > 0;
    const hasWarnings = (importResult?.customersWarn?.length ?? 0) > 0;
    const hasOtherWarnings = (importResult?.otherWarnings?.length ?? 0) > 0;

    return (
        <Container>
            <Header>
                <HeaderLeft>
                    <Title text="customer.modal.import.report.result" />
                    {state === 'ERROR' && errorDetails.length > 0 ? (
                        <Text text={errorDetails[0]?.line ? '' : 'customer.modal.import.result.genericError'} />
                    ) : null}
                </HeaderLeft>
                <HeaderRight data-testid="section-right-header">
                    <StyledText state={state} type="small" text={stateText} />
                </HeaderRight>
            </Header>

            {/* Stats - shown on success */}
            {importResult && state === 'SUCCESS' && (
                <StatsList>
                    <StatItem>
                        {computeText(intl, 'customer.modal.import.result.created', {
                            count: importResult.customersCreated,
                        })}
                    </StatItem>
                    <StatItem>
                        {computeText(intl, 'customer.modal.import.result.updated', {
                            count: importResult.customersUpdated,
                        })}
                    </StatItem>
                    <StatItem>
                        {computeText(intl, 'customer.modal.import.result.unchanged', {
                            count: importResult.customersUnchanged,
                        })}
                    </StatItem>
                </StatsList>
            )}

            {/* Validation errors (from failed import) */}
            {errorDetails.length > 0 && (
                <ErrorList>
                    {errorDetails.map((detail, index) => (
                        <ErrorItem key={index}>{formatErrorLine(detail)}</ErrorItem>
                    ))}
                </ErrorList>
            )}

            {/* Errors section (from successful import with some field errors) */}
            {hasErrors && importResult?.customersError && importResult.customersError.length > 0 && (
                <>
                    <SectionTitle variant="error">
                        {computeText(intl, 'customer.modal.import.result.errors')}
                    </SectionTitle>
                    <ErrorList>
                        {importResult.customersError.map((item, index) => (
                            <ErrorItem key={index}>{formatCustomerError(item)}</ErrorItem>
                        ))}
                    </ErrorList>
                </>
            )}

            {/* Warnings section */}
            {(hasWarnings || hasOtherWarnings) && (
                <>
                    <SectionTitle variant="warning">
                        {computeText(intl, 'customer.modal.import.result.warnings')}
                    </SectionTitle>
                    <WarningList>
                        {importResult?.customersWarn?.map((item, index) => (
                            <WarningItem key={index}>{formatCustomerError(item)}</WarningItem>
                        ))}
                        {importResult?.otherWarnings?.map((warn, index) => (
                            <WarningItem key={`other-${index}`}>
                                {computeText(intl, `customer.modal.import.result.otherWarning.${warn.type}`)}
                                {warn.list.join(', ')}
                            </WarningItem>
                        ))}
                    </WarningList>
                </>
            )}

            {/* Info message about restricted fields */}
            {(hasErrors || hasWarnings || hasOtherWarnings) && (
                <Text text="customer.modal.import.result.restrictedFieldsInfo" type="small" />
            )}
        </Container>
    );
};
